import { SuperUser } from './super-user-entity';

export enum CLIENT_PLAN {
  STARTER = 'STARTER',
  CORE = 'CORE',
  PROFESSIONAL = 'PROFESSIONAL',
  ENTERPRISE = 'ENTERPRISE'
}

export enum IntegrationFlowType {
  TDT = 'TDT'
}

interface ITDTIntegrationSettings {
  collection_time_threshold: number;
}
interface IIntegrationFlow {
  contract?: string;
  enabled: boolean;
  settings?: ITDTIntegrationSettings;
  type?: IntegrationFlowType;
}

export class Notifications {
  email: {
    enabled: boolean;
  };
  sms: {
    api_key_1?: string;
    api_key_2?: string;
    enabled: boolean;
    provider?: string;
  };
  otp: {
    api_key_1?: string;
    enabled: boolean;
    provider?: string;
  };

  constructor(args: any) {
    this.email = {
      enabled: args?.email?.enabled || false
    };
    this.sms = {
      api_key_1: args?.sms?.api_key_1 || '',
      api_key_2: args?.sms?.api_key_2 || '',
      enabled: args?.sms?.enabled || false,
      provider: args?.sms?.provider || ''
    };
    this.otp = {
      api_key_1: args?.otp?.api_key_1 || '',
      enabled: args?.otp?.enabled || false,
      provider: args?.otp?.provider || 'cmcom'
    };
  }
}

export class PersistDriverLocation {
  enabled: boolean;

  constructor(args: any) {
    this.enabled = args.enabled || false;
  }
}

export class HubSettings {
  cross_clustering: {
    enabled: boolean;
  };

  constructor(args: any) {
    this.cross_clustering = {
      enabled: args?.hub?.cross_clustering?.enabled || false
    };
  }
}

export class Client {
  id: string;
  name: string;
  organization: string;
  logo: string;
  client_type: string;
  super_user: SuperUser;
  log_out_drivers: boolean;
  qr_typing_rights: boolean;
  secondary_driver_rights: boolean;
  wallet_feature: boolean;
  issuer_public_key: string;
  use_third_party_api: boolean;
  logo_enabled: boolean;
  ratings_enabled: boolean;
  all_driver_location_updates: boolean;
  allow_custom_payment_fields?: boolean;
  partners?: [];
  users?: [];
  google_maps_api_enabled?: boolean;
  timezone?: string;
  route_planning_enabled: boolean;
  customer_details_database?: boolean;
  integration_flow: IIntegrationFlow;
  delivery_map_enabled?: boolean;
  app_chat_bot?: boolean;
  flightboard_enabled?: boolean;
  currency?: string;
  settings: {
    driver: {
      password_reset: boolean;
    };
    notifications: Notifications;
    tracking: {
      logo_enabled: boolean;
      ratings_enabled: boolean;
    };
    route_planning: {
      route_planning_enabled: boolean;
    };
    persist_driver_location: PersistDriverLocation;
    hub: HubSettings;
    route_optimisation: {
      return_geometry: boolean;
    };
  };
  plan?: CLIENT_PLAN;

  constructor(args: any) {
    this.id = args.id;
    this.name = args.name;
    this.organization = args.organization;
    this.logo = args.logo;
    this.client_type = args.client_type || '';
    this.super_user = args.super_user && new SuperUser(args.super_user);
    this.log_out_drivers = args.log_out_drivers || false;
    this.qr_typing_rights = args.qr_typing_rights || false;
    this.secondary_driver_rights = args.secondary_driver_rights || false;
    this.wallet_feature = args.wallet_feature || false;
    this.issuer_public_key = args.issuer_public_key || '';
    this.use_third_party_api = args.use_third_party_api || false;
    this.logo_enabled = args.logo_enabled || false;
    this.ratings_enabled = args.ratings_enabled || false;
    this.all_driver_location_updates = args.all_driver_location_updates || false;
    this.allow_custom_payment_fields = args.allow_custom_payment_fields || false;
    this.partners = args.partners;
    this.users = args.users;
    this.google_maps_api_enabled = args.google_maps_api_enabled || false;
    this.timezone = args.timezone || undefined;
    this.route_planning_enabled = args.route_planning_enabled || false;
    this.customer_details_database = args.customer_details_database || false;
    this.integration_flow = args.integration_flow ?? {
      enabled: false
    };
    this.delivery_map_enabled = args.delivery_map_enabled || false;
    this.app_chat_bot = args.app_chat_bot || false;
    this.currency = args.currency || undefined;
    this.settings = {
      // This entity rebuilds settings key by key, so anything not copied here never reaches the
      // store — the Driver OTP row read settings.driver.password_reset and always saw undefined.
      driver: {
        password_reset: args.settings?.driver?.password_reset || false
      },
      notifications: new Notifications(args.settings?.notifications || {}),
      tracking: args.settings?.tracking,
      route_planning: args.settings?.route_planning,
      persist_driver_location: new PersistDriverLocation(
        args.settings?.persist_driver_location || { enabled: false }
      ),
      hub: {
        cross_clustering: {
          enabled: args.settings?.hub?.cross_clustering?.enabled || false
        }
      },
      route_optimisation: {
        return_geometry: args.settings?.route_optimisation?.return_geometry || false
      }
    };
    this.plan = args.plan || undefined;
    this.flightboard_enabled = args.flightboard_enabled || false;
  }
}
